'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Bell, BookmarkPlus, CheckCircle, Loader2, LogIn } from 'lucide-react';
import { usePlanningSearch } from '../state/PlanningSearchContext';
import { createAlert, quoteAlert } from '@/lib/accountsAlerts';
import { AccountsApiError, getEmailFromIdToken } from '@/lib/apiAccounts';
import { createAuthPath, getCurrentAuthReturnTo } from '@/lib/authNavigation';
import { trackCoreJourneyEvent } from '@/lib/coreJourneyAnalytics';
import { PLANNING_ALERTS_ENABLED } from '@/lib/planningAlertsFeatureFlag';
import {
  clearPendingPlanningAlert,
  loadPendingPlanningAlert,
  planningAlertResumeReturnTo,
  savePendingPlanningAlert,
  type PendingPlanningAlert,
} from '@/lib/pendingPlanningAlert';
import type { PlanningAlertCriteria, PlanningAlertQuote } from '@/types/alerts';

interface SaveSearchDialogProps {
  open: boolean;
  onClose: () => void;
  initialIntent?: 'save' | 'alert';
}

const summarizeCriteria = (criteria: PlanningAlertCriteria | null): string[] => {
  if (!criteria) return [];
  const lines = [criteria.area.type === 'council' ? `Whole council: ${criteria.area.label}` : `Within ${criteria.area.radiusKm === 0 ? '50m' : `${criteria.area.radiusKm}km`} of ${criteria.area.label}`];
  if (criteria.query) lines.push(`Query: ${criteria.query}`);
  if (criteria.categories?.length) lines.push(`Categories: ${criteria.categories.join(', ')}`);
  if (criteria.tags?.length) lines.push(`Tags: ${criteria.tags.join(', ')}`);
  if (criteria.statuses?.length) lines.push(`Statuses: ${criteria.statuses.join(', ')}`);
  if (criteria.postcodePrefixes?.length) lines.push(`Postcodes: ${criteria.postcodePrefixes.join(', ')}`);
  if (criteria.impactMin !== undefined || criteria.impactMax !== undefined) {
    lines.push(`Impact: ${criteria.impactMin ?? 0}-${criteria.impactMax ?? 10}`);
  }
  if (criteria.hasDocuments !== undefined) lines.push(criteria.hasDocuments ? 'Has documents' : 'No documents');
  if (criteria.hasApplicationForm !== undefined) lines.push(criteria.hasApplicationForm ? 'Has form data' : 'No form data');
  return lines;
};

const SaveSearchDialog: React.FC<SaveSearchDialogProps> = ({ open, onClose, initialIntent = 'save' }) => {
  const { saveCurrentSearch, buildCurrentAlertCriteria } = usePlanningSearch();
  const [name, setName] = useState('');
  const [createEmailAlert, setCreateEmailAlert] = useState(PLANNING_ALERTS_ENABLED && initialIntent === 'alert');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quote, setQuote] = useState<PlanningAlertQuote | null>(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [authStatus, setAuthStatus] = useState<'checking' | 'signed-in' | 'signed-out'>('checking');
  const [resumedPending, setResumedPending] = useState<PendingPlanningAlert | null>(null);
  const startedTrackedRef = useRef(false);

  const currentCriteria = useMemo(() => buildCurrentAlertCriteria(), [buildCurrentAlertCriteria]);
  const criteria = resumedPending?.criteria ?? currentCriteria;
  const criteriaLines = useMemo(() => summarizeCriteria(criteria), [criteria]);

  useEffect(() => {
    if (!open) return;
    setCreateEmailAlert(PLANNING_ALERTS_ENABLED && initialIntent === 'alert');
    setFeedback(null);
    setError(null);
    setQuote(null);
    setSaving(false);
    const pending = PLANNING_ALERTS_ENABLED && initialIntent === 'alert' ? loadPendingPlanningAlert() : null;
    setResumedPending(pending);
    if (pending) {
      setName(pending.name);
      trackCoreJourneyEvent('planning_research', 'planning_alert_resumed', {
        source: pending.source,
        placement: 'save_search_dialog',
        status: 'resumed',
      });
    }
    if (PLANNING_ALERTS_ENABLED && initialIntent === 'alert' && !startedTrackedRef.current) {
      startedTrackedRef.current = true;
      trackCoreJourneyEvent('planning_research', 'planning_alert_create_started', {
        source: pending?.source || 'planning_search',
        placement: 'save_search_dialog',
        status: pending ? 'resumed' : 'opened',
      });
    }
  }, [initialIntent, open]);

  useEffect(() => {
    if (!open) startedTrackedRef.current = false;
  }, [open]);

  useEffect(() => {
    if (!open || !PLANNING_ALERTS_ENABLED || !createEmailAlert) return;
    let cancelled = false;

    const loadQuote = async () => {
      setLoadingQuote(true);
      setError(null);
      try {
        const me = await getEmailFromIdToken();
        if (!me?.email) {
          if (!cancelled) setAuthStatus('signed-out');
          return;
        }
        if (!cancelled) setAuthStatus('signed-in');
        if (!criteria) {
          if (!cancelled) setQuote(null);
          return;
        }
        const nextQuote = await quoteAlert(criteria);
        if (!cancelled) {
          setQuote(nextQuote);
          trackCoreJourneyEvent('planning_research', 'planning_alert_quote_succeeded', {
            source: resumedPending?.source || 'planning_search',
            placement: 'save_search_dialog',
            status: 'quoted',
            metadata: {
              credit_cost: nextQuote.creditCost,
              estimated_recent_matches: nextQuote.estimatedRecentMatches,
            },
          });
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof AccountsApiError && err.status === 401) {
          setAuthStatus('signed-out');
          return;
        }
        trackCoreJourneyEvent('planning_research', 'planning_alert_quote_failed', {
          source: resumedPending?.source || 'planning_search',
          placement: 'save_search_dialog',
          status: err instanceof AccountsApiError ? `http_${err.status}` : 'request_failed',
        });
        setError(err instanceof Error ? err.message : 'Unable to prepare this alert.');
      } finally {
        if (!cancelled) setLoadingQuote(false);
      }
    };

    loadQuote();
    return () => {
      cancelled = true;
    };
  }, [criteria, createEmailAlert, open, resumedPending?.source]);

  const handleAuth = useCallback((mode: 'sign-in' | 'sign-up') => {
    if (!criteria) {
      clearPendingPlanningAlert();
      setError('Choose a location before creating an alert.');
      return;
    }
    const pending = savePendingPlanningAlert({
      name: name.trim() || 'Planning alert',
      criteria,
      source: 'planning_search',
    });
    trackCoreJourneyEvent('planning_research', 'planning_alert_auth_required', {
      source: 'planning_search',
      placement: 'save_search_dialog',
      status: pending ? mode : 'storage_unavailable',
    });
    window.location.assign(
      createAuthPath(
        mode,
        pending ? planningAlertResumeReturnTo('/planning-updates') : getCurrentAuthReturnTo('/planning-updates'),
      ),
    );
  }, [criteria, name]);

  const handleSave = useCallback(async () => {
    const shouldCreateAlert = PLANNING_ALERTS_ENABLED && createEmailAlert;
    const trimmedName = name.trim() || (shouldCreateAlert ? 'Planning alert' : 'Saved search');
    setSaving(true);
    setError(null);
    setFeedback(null);
    try {
      if (shouldCreateAlert) {
        if (!criteria) {
          throw new Error('Choose a location before creating an alert.');
        }
        await createAlert({ name: trimmedName, criteria });
        saveCurrentSearch(trimmedName, { enableAlerts: true });
        trackCoreJourneyEvent('planning_research', 'planning_alert_created', {
          source: 'planning_search',
          placement: 'save_search_dialog',
          status: 'created',
          metadata: {
            name_length: trimmedName.length,
            credit_cost: quote?.creditCost ?? 1,
            estimated_recent_matches: quote?.estimatedRecentMatches ?? null,
          },
        });
        clearPendingPlanningAlert();
        setResumedPending(null);
        setFeedback('Alert created. New matching applications will be emailed to you.');
      } else {
        saveCurrentSearch(trimmedName, { enableAlerts: false });
        trackCoreJourneyEvent('planning_research', 'search_saved', {
          source: 'planning_search',
          placement: 'save_search_dialog',
          status: 'saved',
          metadata: { name_length: trimmedName.length },
        });
        setFeedback('Search saved. You can return to it from this browser.');
      }
      setTimeout(() => onClose(), 900);
    } catch (err) {
      if (err instanceof AccountsApiError && err.status === 401) {
        setAuthStatus('signed-out');
        setError('Sign in to create alerts.');
      } else {
        setError(err instanceof Error ? err.message : 'Unable to save this search.');
      }
      if (shouldCreateAlert) {
        trackCoreJourneyEvent('planning_research', 'planning_alert_create_failed', {
          source: resumedPending?.source || 'planning_search',
          placement: 'save_search_dialog',
          status: err instanceof AccountsApiError ? `http_${err.status}` : 'request_failed',
        });
      }
    } finally {
      setSaving(false);
    }
  }, [createEmailAlert, criteria, name, onClose, quote, resumedPending?.source, saveCurrentSearch]);

  const handleClose = useCallback(() => {
    if (resumedPending) clearPendingPlanningAlert();
    setResumedPending(null);
    onClose();
  }, [onClose, resumedPending]);

  if (!open) return null;

  const cannotCreateAlert = PLANNING_ALERTS_ENABLED && createEmailAlert && (!criteria || authStatus === 'signed-out' || loadingQuote || saving || (quote?.creditSummary.available ?? 1) < 1);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)] p-6 shadow-2xl">
        <h2 className="text-lg font-semibold text-[var(--c-text-primary)]">
          {createEmailAlert ? 'Create planning alert' : 'Save this search'}
        </h2>
        <p className="mt-1 text-sm text-[var(--c-text-secondary)]">
          {PLANNING_ALERTS_ENABLED
            ? 'Save the current filters, or turn them into an email alert for newly added matching applications.'
            : 'Save the current filters so you can return to this search later.'}
        </p>

        <label className="mt-4 block text-sm text-[var(--c-text-secondary)]">
          Name
          <input
            type="text"
            value={name}
            maxLength={80}
            onChange={(event) => setName(event.target.value)}
            placeholder={createEmailAlert ? 'e.g. Leith PBSA alert' : 'e.g. City centre commercial'}
            className="mt-1 w-full rounded-lg border border-[var(--c-border)] bg-transparent px-3 py-2 text-[var(--c-text-primary)] focus:border-[var(--c-accent)] focus:outline-none"
          />
        </label>

        {PLANNING_ALERTS_ENABLED && (
          <div className="mt-4 grid grid-cols-2 gap-2 rounded-lg border border-[var(--c-border)] p-1">
            <button
              type="button"
              onClick={() => setCreateEmailAlert(false)}
              className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm ${!createEmailAlert ? 'bg-[var(--c-accent)] text-[var(--c-bg)]' : 'text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]'}`}
            >
              <BookmarkPlus className="h-4 w-4" aria-hidden />
              Save only
            </button>
            <button
              type="button"
              onClick={() => setCreateEmailAlert(true)}
              className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm ${createEmailAlert ? 'bg-[var(--c-accent)] text-[var(--c-bg)]' : 'text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]'}`}
            >
              <Bell className="h-4 w-4" aria-hidden />
              Email alert
            </button>
          </div>
        )}

        {PLANNING_ALERTS_ENABLED && createEmailAlert && (
          <div className="mt-4 rounded-lg border border-[var(--c-border)] bg-[var(--c-bg)] p-4">
            {!criteria ? (
              <div className="flex items-start gap-2 text-sm text-[var(--c-text-secondary)]">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-none text-[var(--c-accent)]" aria-hidden />
                Choose a location and radius before creating an alert.
              </div>
            ) : (
              <>
                <div className="space-y-1 text-sm text-[var(--c-text-secondary)]">
                  {criteriaLines.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                </div>
                <div className="mt-3 border-t border-[var(--c-border)] pt-3 text-sm">
                  {authStatus === 'signed-out' ? (
                    <div className="space-y-3">
                      <p className="text-[var(--c-text-secondary)]">Sign in to reserve an alert credit.</p>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => handleAuth('sign-in')} className="inline-flex items-center gap-2 rounded-md border border-[var(--c-border)] px-3 py-2 text-[var(--c-text-primary)]">
                          <LogIn className="h-4 w-4" aria-hidden />
                          Sign in
                        </button>
                        <button type="button" onClick={() => handleAuth('sign-up')} className="rounded-md bg-[var(--c-accent)] px-3 py-2 text-[var(--c-bg)]">
                          Create account
                        </button>
                      </div>
                    </div>
                  ) : loadingQuote ? (
                    <div className="flex items-center gap-2 text-[var(--c-text-secondary)]">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      Checking credits...
                    </div>
                  ) : quote ? (
                    <div className="grid gap-2 text-[var(--c-text-secondary)] sm:grid-cols-3">
                      <div>
                        <div className="text-xs uppercase tracking-wide">Cost</div>
                        <div className="font-semibold text-[var(--c-text-primary)]">{quote.creditCost} credit</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide">Available</div>
                        <div className="font-semibold text-[var(--c-text-primary)]">
                          {Math.max(0, quote.creditSummary.available - quote.creditCost)} after save
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide">Recent matches</div>
                        <div className="font-semibold text-[var(--c-text-primary)]">{quote.estimatedRecentMatches}</div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </>
            )}
          </div>
        )}

        {(feedback || error || quote?.warnings?.length) && (
          <div className={`mt-3 rounded-lg border px-3 py-2 text-sm ${error ? 'border-[rgba(140,74,62,0.4)] text-[var(--c-error)]' : 'border-[var(--c-border)] text-[var(--c-text-secondary)]'}`}>
            {feedback && (
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-[var(--c-accent)]" aria-hidden />
                {feedback}
              </div>
            )}
            {error && <div>{error}</div>}
            {!error && quote?.warnings?.map((warning) => <div key={warning}>{warning}</div>)}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md px-4 py-2 text-sm text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]"
          >
            Cancel
          </button>
          <button
            type="button"
            data-analytics-event={createEmailAlert ? 'planning_alert_create_confirm_clicked' : 'search_save_confirm_clicked'}
            data-analytics-label={createEmailAlert ? 'planning_alert_create_confirm' : 'planning_search_save_confirm'}
            onClick={handleSave}
            disabled={cannotCreateAlert || saving}
            className="inline-flex items-center gap-2 rounded-md bg-[var(--c-accent)] px-4 py-2 text-sm font-medium text-[var(--c-bg)] hover:bg-[var(--c-accent)]/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {createEmailAlert ? 'Create alert' : 'Save search'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveSearchDialog;
